import React, { useState } from 'react';
import { BellIcon, MailIcon, PhoneIcon, SmartphoneIcon, MessageSquareIcon } from 'lucide-react';
interface NotificationSettingsProps {
  persona: 'patient' | 'receptionist';
}
export function NotificationSettings({
  persona
}: NotificationSettingsProps) {
  const [settings, setSettings] = useState({
    email: {
      enabled: true,
      newAppointment: true,
      reminder24h: true,
      reminder1h: true,
      cancellation: true,
      rescheduling: true
    },
    sms: {
      enabled: true,
      newAppointment: true,
      reminder24h: true,
      reminder1h: false,
      cancellation: true,
      rescheduling: true
    },
    phone: {
      enabled: false,
      newAppointment: false,
      reminder24h: true,
      reminder1h: false,
      cancellation: true,
      rescheduling: false
    },
    push: {
      enabled: true,
      newAppointment: true,
      reminder24h: true,
      reminder1h: true,
      cancellation: true,
      rescheduling: true
    }
  });
  const toggleChannel = (channel: keyof typeof settings) => {
    setSettings({
      ...settings,
      [channel]: {
        ...settings[channel],
        enabled: !settings[channel].enabled
      }
    });
  };
  const toggleNotification = (channel: keyof typeof settings, notification: string) => {
    setSettings({
      ...settings,
      [channel]: {
        ...settings[channel],
        [notification]: !settings[channel][notification as keyof (typeof settings)[typeof channel]]
      }
    });
  };
  const channels = [{
    key: 'email' as const,
    name: 'Email Notifications',
    icon: MailIcon,
    color: 'text-blue-600'
  }, {
    key: 'sms' as const,
    name: 'SMS Notifications',
    icon: MessageSquareIcon,
    color: 'text-green-600'
  }, {
    key: 'phone' as const,
    name: 'Phone Calls',
    icon: PhoneIcon,
    color: 'text-purple-600'
  }, {
    key: 'push' as const,
    name: 'Mobile Push Notifications',
    icon: SmartphoneIcon,
    color: 'text-orange-600'
  }];
  const notificationTypes = [{
    key: 'newAppointment',
    label: 'New Appointment Confirmation'
  }, {
    key: 'reminder24h',
    label: '24-Hour Reminder'
  }, {
    key: 'reminder1h',
    label: '1-Hour Reminder'
  }, {
    key: 'cancellation',
    label: 'Cancellation Alerts'
  }, {
    key: 'rescheduling',
    label: 'Rescheduling Notifications'
  }];
  return <div className="space-y-6" data-id="element-179">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-180">
        <div className="flex items-center space-x-3 mb-6" data-id="element-181">
          <BellIcon className="w-6 h-6 text-blue-600" data-id="element-182" />
          <h2 className="text-2xl font-bold" data-id="element-183">Notification Preferences</h2>
        </div>
        <p className="text-gray-600 mb-6" data-id="element-184">
          Configure how you want to receive appointment notifications across
          different channels.
        </p>
        <div className="space-y-6" data-id="element-185">
          {channels.map(channel => {
          const Icon = channel.icon;
          const channelSettings = settings[channel.key];
          return <div key={channel.key} className="border border-gray-200 rounded-lg p-6" data-id="element-186">
                <div className="flex items-center justify-between mb-4" data-id="element-187">
                  <div className="flex items-center space-x-3" data-id="element-188">
                    <Icon className={`w-6 h-6 ${channel.color}`} data-id="element-189" />
                    <h3 className="text-lg font-semibold" data-id="element-190">{channel.name}</h3>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-191">
                    <input type="checkbox" checked={channelSettings.enabled} onChange={() => toggleChannel(channel.key)} className="sr-only peer" data-id="element-192" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-193"></div>
                  </label>
                </div>
                {channelSettings.enabled && <div className="ml-9 space-y-3" data-id="element-194">
                    {notificationTypes.map(type => <label key={type.key} className="flex items-center space-x-3" data-id="element-195">
                        <input type="checkbox" checked={channelSettings[type.key as keyof typeof channelSettings] as boolean} onChange={() => toggleNotification(channel.key, type.key)} className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500" data-id="element-196" />
                        <span className="text-sm text-gray-700" data-id="element-197">
                          {type.label}
                        </span>
                      </label>)}
                  </div>}
              </div>;
        })}
        </div>
        <div className="mt-6 flex justify-end" data-id="element-198">
          <button className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-199">
            Save Preferences
          </button>
        </div>
      </div>
      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-200">
        <h3 className="text-lg font-semibold mb-4" data-id="element-201">Contact Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-202">
          <div data-id="element-203">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-204">
              Email Address
            </label>
            <input type="email" placeholder="your.email@example.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-205" />
          </div>
          <div data-id="element-206">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-207">
              Phone Number
            </label>
            <input type="tel" placeholder="+1 (555) 000-0000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-208" />
          </div>
        </div>
        <div className="mt-4 flex justify-end" data-id="element-209">
          <button className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700" data-id="element-210">
            Update Contact Info
          </button>
        </div>
      </div>
    </div>;
}